/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  CSSProperties,
  forwardRef,
  memo,
  NamedExoticComponent,
  ReactElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';
import {areEqual} from 'react-window';
import Link from '@material-ui/core/Link';
import {makeStyles} from '@material-ui/core/styles';
import isString from 'lodash.isstring';
import {translate} from '@ion/core/src/locale/locale';
import {GlobalGroupProps} from '@ion/core/src/types/general';
import {NavigationIcon} from '@ion/dls/src/icons/NavigationIcon';
import Breadcrumb from '../../components/breadcrumbs/Breadcrumbs';
import ClickAwayListener from '../../components/listeners/ClickAwayListener';
import Hidden from '../../layout/Hidden';
import IonPortal from '../../render/IonPortal';
import {dlsColor, dlsLayout, dlsZIndex} from '../../theme/theme';
import {Color, CreateGeoFenceTypeEnum} from '../../types/types';
import {OMIT_CLICK_AWAY_LISTENER} from '../constants';
import {removeActiveFence} from '../helpers/MapViewUtils';
import {EnterGeofenceLocation} from './EnterGeofenceLocation';
import {GeofencesList} from './GeofencesList';

type CreateGeofencesProps = GlobalGroupProps & {
  map: __esri.Map;
  mapView: __esri.MapView;
  show: boolean;
  isPhone: boolean;
  handleViewAllClick?: Function;
  assetCategories?: string[];
  geofenceSave?: Function;
  savingGeofence?: boolean;
  geoFencesGraphicsLayerRef?: {
    current: null | __esri.Layer;
  };
  sketchViewModelRef?: {
    current: null | __esri.SketchViewModel;
  };
  selectedMapPointRef?: {
    current: null | __esri.Layer;
  };
  editGeofenceGraphic?: __esri.Graphic;
  setEditGeofenceGraphic?: Function;
  MapControlsBreadCrumbs?: null | ReactElement | NamedExoticComponent;
  isShowViewAllOption?: boolean;
  closeGeoFencePanel?: Function;
  onClose?: () => void;
  isBoundaryCreation?: boolean;
  selectedDrawingTool?: CreateGeoFenceTypeEnum;
  onApplyBoundary?: Function;
  createPopupStyle?: CSSProperties;
  isAdminGeofence?: boolean;
  setIsGeofenceContentsOpen?: Function;
  useHereApi?: boolean;
  usePortal?: boolean;
  moveAllToolsToTop?: boolean;
  isFMTracking?: boolean;
  toggleShowBoundaryPanel?: Function;
  rootTitle?: string;
};

type StyleProps = {
  moveAllToolsToTop?: boolean;
  isBoundaryCreation?: boolean;
  isAdminGeofence?: boolean;
};

const toggleBoundaryStyle = (opt1, opt2, opt3) =>
  ({isBoundaryCreation, moveAllToolsToTop}: StyleProps) => isBoundaryCreation ? opt1 : moveAllToolsToTop ? opt2 : opt3
;

const useStyles = makeStyles(theme => {
  const {mobile} = dlsLayout('breakpoints');
  return {
    wrapper : {
      marginLeft                     : 0,
      maxWidth                       : ({isAdminGeofence}: StyleProps) => isAdminGeofence ? '100vh' : 285,
      width                          : ({isAdminGeofence}: StyleProps) => isAdminGeofence ? 325 : 285,
      padding                        : 0,
      background                     : dlsColor('white'),
      marginTop                      : '14px',
      top                            : ({isBoundaryCreation}: {isBoundaryCreation: boolean}) => isBoundaryCreation ? 0 : '96px',
      right                          : toggleBoundaryStyle(0, '135px', '50px'),
      position                       : 'absolute',
      zIndex                         : dlsZIndex('floatingActionButtonDisplay'),
      [theme.breakpoints.down('sm')] : {
        width     : '100%',
        maxWidth  : 'none',
        top       : 100,
        marginTop : 'inherit'
      }
    },
    container : {
      [theme.breakpoints.down('md')] : {
        padding : '0px 8px 36px 0px'
      },
      [theme.breakpoints.up('md')] : {
        maxHeight : ({isAdminGeofence}: StyleProps) => isAdminGeofence ? 350 : 400,
        overflowX : 'hidden',
        overflowY : 'auto'
      },
      background : dlsColor('white')
    },
    inactiveCrumb : {
      color    : dlsColor('white'),
      cursor   : 'pointer',
      fontSize : '12px'
    },
    activeCrumb : {
      color    : dlsColor('white'),
      fontSize : '12px'
    },
    breadcrumb : {
      padding       : 0,
      background    : dlsColor('grey10'),
      textTransform : 'uppercase'
    },
    checkbox : {
      marginTop    : '10px',
      marginBottom : '10px',
      display      : 'block'
    },
    breadcrumbSeparator : {
      color    : dlsColor('white'),
      fontSize : '12px'
    }
  };
});

export const CreateGeofences = forwardRef(({
  map,
  mapView,
  show,
  isPhone,
  handleViewAllClick,
  assetCategories,
  geofenceSave,
  accountMap,
  globalGroup,
  savingGeofence,
  geoFencesGraphicsLayerRef,
  sketchViewModelRef,
  selectedMapPointRef,
  editGeofenceGraphic,
  setEditGeofenceGraphic,
  MapControlsBreadCrumbs,
  isShowViewAllOption,
  closeGeoFencePanel,
  onClose,
  selectedDrawingTool,
  onApplyBoundary,
  toggleShowBoundaryPanel,
  createPopupStyle,
  setIsGeofenceContentsOpen,
  rootTitle = translate('maps:geofences.rootTitle'),
  isBoundaryCreation = false,
  isAdminGeofence = false,
  useHereApi = false,
  usePortal = false,
  moveAllToolsToTop = false,
  isFMTracking = false
}: CreateGeofencesProps, ref) => {
  const classes = useStyles({isBoundaryCreation, isAdminGeofence, moveAllToolsToTop});
  const [
    selectedGeofenceType,
    setSelectedGeofenceType
  ] = useState<CreateGeoFenceTypeEnum | null>(selectedDrawingTool);
  const [isMain, setIsMain]                   = useState(false);
  const [isContents, setIsContents]           = useState(false);
  const [crumbs, setCrumbs]                   = useState([]);
  const [displayValues, setDisplayValues]     = useState([]);
  const [isBoundary, setIsBoundary]           = useState(false);
  const [boundary, setBoundary]               = useState('');
  const [clickNext, setClickNext]             = useState(false);
  const [mapClickHandler, setMapClickHandler] = useState(null);

  const shouldResetCrumbRef = useRef(false);

  const mapViewEl = document.querySelector('.esri-view-surface');

  useEffect(() => {
    handleDefault(rootTitle);
    defineBreadCrumbChildren(crumbs);
  }, []);

  useEffect(() => {
    // Hook to reset the crumbs if panel is closed after saving
    if (!show && crumbs.length > 1) {
      selectCrumbs(0, crumbs);
    }
  }, [show]);

  useEffect(() => {
    if (isBoundaryCreation && selectedDrawingTool) {
      setIsContents(true);
      shouldResetCrumbRef.current = true;
      changeSelectedGeofenceType(selectedDrawingTool);
    }
  }, [isBoundaryCreation, selectedDrawingTool]);

  useEffect(() => {
    if (editGeofenceGraphic) {
      setIsMain(false);
      setIsContents(true);
      setSelectedGeofenceType(CreateGeoFenceTypeEnum.Draw);
    }
  }, [editGeofenceGraphic]);

  useEffect(() => {
    setIsGeofenceContentsOpen?.(isContents);
  }, [isContents]);

  useImperativeHandle(ref, () => ({
    handleBack
  }));

  const handleBack = useCallback(() => {
    mapViewEl?.classList.remove('crosshair');
    mapViewEl?.classList.remove('crosshair-only');
    mapViewEl?.classList.add('default-cursor');
    removeActiveFence(
      map,
      [
        geoFencesGraphicsLayerRef,
        sketchViewModelRef,
        selectedMapPointRef
      ]
    );
    mapClickHandler?.remove();
    if (crumbs.length > 1) {
      removeBreadCrumb(crumbs);
    } else if (crumbs.length === 1) {
      setIsMain(true);
    }
  }, [mapClickHandler, crumbs]);

  const handleDefault = useCallback(crumb => {
    const temp = {label: crumb, method: function(param) { setIsMain(true); }};
    breadCrumbChildren(temp, crumbs);
    setIsMain(true);
  }, [crumbs]);

  const onFenceSelected = useCallback(crumb => {
    const temp = {
      label  : crumb,
      method : function(param) { setIsContents(param); }
    };
    breadCrumbChildren(temp, crumbs);
    setIsMain(false);
    setIsContents(true);
  }, [crumbs]);

  const onBoundarySelected = useCallback(crumb => {
    const temp = {
      label  : crumb,
      method : function(param) { setIsBoundary(param); }
    };
    breadCrumbChildren(temp, crumbs);
    setIsContents(false);
    setIsBoundary(true);
    setBoundary(crumb);
  }, [crumbs]);

  const breadCrumbChildren = useCallback((label, crumbs) => {
    let subCrumbs = crumbs;
    if (crumbs.length > 1 && shouldResetCrumbRef.current) {
      shouldResetCrumbRef.current = false;
      subCrumbs = [crumbs[0]];
    }
    subCrumbs.push(label);
    setCrumbs(subCrumbs);
    defineBreadCrumbChildren(subCrumbs);
  }, []);

  const onBack = useCallback(function() {
    handleBack();
    toggleShowBoundaryPanel?.();
  }, [handleBack, toggleShowBoundaryPanel]);

  const defineBreadCrumbChildren = useCallback(crumbs => {
    const displaycrumbs = [];
    crumbs.map((element, index) => {
      displaycrumbs.push(
        <Link key={index} style={{textDecoration: 'none'}}
          className={(crumbs.length - 1) === index
            ? classes.activeCrumb : classes.inactiveCrumb}
          onClick={(crumbs.length - 1) !== index ? onBack : null}>
          {index === 0
            ? element.label
            : translate(`maps:geofences.${element?.label?.toLowerCase()}`)}
        </Link>);
    });
    setDisplayValues(displaycrumbs);
  }, []);

  const selectCrumbs = useCallback((index, crumbs) => {
    const temp = crumbs;
    temp.forEach((element, i) => {
      if (i === 0 && (index === i)) {
        element.method();
        setSelectedGeofenceType(null);
      } else {
        if (i === index) {
          element.method(true);
        } else if (i !== 0) {
          element.method(false);
        }
      }
    });
    temp.splice(index + 1, temp.length);
    setCrumbs(temp);
    defineBreadCrumbChildren(crumbs);
    if (setEditGeofenceGraphic) {
      setEditGeofenceGraphic(null);
    }
    if (isBoundaryCreation) {
      closeGeoFencePanel();
    }
  }, [isBoundaryCreation]);

  const removeBreadCrumb = useCallback(crumbs => {
    const temp = crumbs;
    const removed = temp.splice(temp.length - 1, temp.length);
    setCrumbs(temp);
    if (removed.length > 0) {
      removed[0].method(false);
      if (crumbs.length === 1) {
        setSelectedGeofenceType(null);
      }
    }
    if (temp.length > 0) {
      temp[(temp.length - 1)].method(true);
    }
    defineBreadCrumbChildren(crumbs);
    if (isBoundaryCreation) {
      closeGeoFencePanel();
    }
  }, [isBoundaryCreation]); ;

  const changeSelectedGeofenceType = useCallback((value: CreateGeoFenceTypeEnum) => {
    setClickNext(false);
    onFenceSelected(value);
    setSelectedGeofenceType(value);
  }, []);

  const draw = useCallback(() => {
    setClickNext(true);
  }, []);

  /**
   * @private
   */
  const shouldClose = useCallback((target, canShow, crumbsList) => {
    if (target) {
      return canShow &&
           (isString(target?.className) &&
           (!target?.className?.includes(OMIT_CLICK_AWAY_LISTENER) &&
           !target?.innerHTML.includes(OMIT_CLICK_AWAY_LISTENER))) &&
           crumbsList?.length <= 1;
    }
    return true;
  }, []);

  const onClickAway = useCallback(function({target}) {
    if (shouldClose(target, show, crumbs)) {
      onClose?.();
    }
  }, [show, crumbs]);

  const createPanelStyle = useMemo(() => ({
    display : show ? isPhone? 'contents' : 'inherit' : 'none',
    ...createPopupStyle
  }), [createPopupStyle, show, isPhone]);

  /** This constant will show main boundary/geofence panel only
   * for geofence panel (desktop & mobile) and boundary (mobile only))
   */
  const isShowMainGeofencePanel = useMemo(() => {
    if (isMain) {
      if (isPhone) {
        return !selectedGeofenceType;
      } else {
        return !selectedGeofenceType && !isBoundaryCreation;
      }
    }
  }, [isPhone, selectedGeofenceType, isBoundaryCreation, isMain]);

  const component = (
    show && (
      <ClickAwayListener onClickAway={onClickAway}
        mountListener>
        <div style={createPanelStyle} className={classes.wrapper}>
          {
            crumbs?.length > 1 && (
              <div className={classes.breadcrumb}>
                <Breadcrumb icon={<NavigationIcon direction='back' color={Color.white} />}
                  className={classes.breadcrumb}
                  color={Color.white}
                  iconClick={onBack}
                  separator={(
                    <span className={classes.breadcrumbSeparator}>
                    -
                    </span>
                  )}
                  height={isPhone && '45px'}>
                  {displayValues}
                </Breadcrumb>
              </div>
            )
          }
          <div className={classes.container}>
            {isShowMainGeofencePanel && (
              <GeofencesList changeSelectedGeofenceType={changeSelectedGeofenceType}
                isBoundaryCreation={isBoundaryCreation}
                handleViewAllClick={handleViewAllClick}
                MapControlsBreadCrumbs={MapControlsBreadCrumbs}
                isShowViewAllOption={isShowViewAllOption}
                isPhone={isPhone}
                isAdminGeofence={isAdminGeofence} />
            )}
            {isContents && (
              <EnterGeofenceLocation selectedGeofenceType={selectedGeofenceType}
                isFMTracking={isFMTracking}
                accountMap={accountMap}
                globalGroup={globalGroup}
                useHereApi={useHereApi}
                onBoundarySelected={onBoundarySelected}
                isBoundary={isBoundary || isBoundaryCreation}
                onApplyBoundary={onApplyBoundary}
                toggleShowBoundaryPanel={toggleShowBoundaryPanel}
                isVisible={show}
                mapView={mapView}
                map={map}
                draw={draw}
                clickNext={clickNext}
                assetCategories={assetCategories}
                closeGeoFencePanel={closeGeoFencePanel}
                geofenceSave={geofenceSave}
                selectCrumbs={selectCrumbs}
                crumbs={crumbs}
                handleBack={handleBack}
                savingGeofence={savingGeofence}
                mapViewEl={mapViewEl}
                editGeofenceGraphic={editGeofenceGraphic}
                geoFencesGraphicsLayerRef={geoFencesGraphicsLayerRef}
                sketchViewModelRef={sketchViewModelRef}
                selectedMapPointRef={selectedMapPointRef}
                mapClickHandler={mapClickHandler}
                setMapClickHandler={setMapClickHandler}
                isPhone={isPhone} />
            )}
            {selectedGeofenceType && isBoundary && boundary === `${translate('maps:geofences.municipalities')}` && (
              <></>
            )}
            {selectedGeofenceType && isBoundary && boundary === `${translate('maps:geofences.area')}` && (
              <></>
            )}
          </div>
        </div>
      </ClickAwayListener>
    ));

  return (
    <Hidden show={isPhone ? 'mobile' : 'desktop'}>
      {usePortal ? (
        <IonPortal id='map-create-geofences' show={show}>
          {component}
        </IonPortal>
      ) : (
        component
      )}
    </Hidden>
  );
});

export default memo(CreateGeofences, areEqual);
